import { LineChart, lineClasses } from '@mui/x-charts/LineChart';
import { QueryResultDto } from '../../../../../../../typescript-axios';
import Box from '@mui/material/Box';
import { AreaChartParameters } from '../config';

interface AreaChartVisualizationProps {
    data: QueryResultDto;
    parameters?: AreaChartParameters;
}

export function AreaChartVisualization({ data, parameters }: AreaChartVisualizationProps) {
    const xAxis = typeof parameters?.x_axis === 'string' ? parameters.x_axis : null;
    const yAxes = Array.isArray(parameters?.y_axes) ? (parameters.y_axes as string[]) : [];
    const showLegend = parameters?.show_legend === true;
    const groupSeriesBy = typeof parameters?.group_series_by === 'string' ? parameters.group_series_by : null;

    if (!xAxis) return <div className="flex align-items-center justify-content-center h-full text-500">Missing x_axis in parameters</div>;
    if (yAxes.length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">Missing y_axes in parameters</div>;
    if ((data?.rows ?? []).length === 0) return <div className="flex align-items-center justify-content-center h-full text-500">No data</div>;

    let series: { label: string; data: (number | null)[]; area: true; stack: string; showMark: false }[];
    let xLabels: string[];

    if (groupSeriesBy) {
        const groupValues = [...new Set(data.rows.map(row => String(row[groupSeriesBy] ?? '')))];
        xLabels = [...new Set(data.rows.map(row => String(row[xAxis] ?? '')))];

        series = groupValues.flatMap(groupVal =>
            yAxes.map(yCol => {
                const rowsByX = new Map(
                    data.rows
                        .filter(row => String(row[groupSeriesBy] ?? '') === groupVal)
                        .map(row => [String(row[xAxis] ?? ''), row])
                );
                return {
                    label: groupValues.length === 1 ? yCol : `${groupVal}${yAxes.length > 1 ? ` – ${yCol}` : ''}`,
                    data: xLabels.map(x => {
                        const row = rowsByX.get(x);
                        if (!row) return null;
                        const val = row[yCol];
                        return val === null || val === undefined ? 0 : Number(val);
                    }),
                    area: true as const,
                    stack: 'total',
                    showMark: false as const,
                };
            })
        );
    } else {
        xLabels = data.rows.map(row => String(row[xAxis] ?? ''));
        series = yAxes.map(col => ({
            label: col,
            data: data.rows.map(row => {
                const val = row[col];
                return val === null || val === undefined ? 0 : Number(val);
            }),
            area: true as const,
            stack: 'total',
            showMark: false as const,
        }));
    }

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <LineChart
                xAxis={[{ scaleType: 'point', data: xLabels }]}
                series={series}
                hideLegend={!showLegend}
                sx={{
                    [`& .${lineClasses.line}`]: {
                        display: 'none',
                    },
                }}
                margin={{ right: 24 }}
            />
        </Box>
    );
}